import React from "react";
import "./CompletionScreen.css";

const CompletionScreen = ({
  firstName,
  onGoToDashboard,
  onLogout,
  stepStatus = {},
  onGoToStep,
  availableSteps,
  onClose,
}) => {
  const handleClose = onClose || onGoToDashboard;

  const allPossibleSteps = [
    { id: "patient",   label: "Patient details verified", done: !stepStatus.patientHasPending, originalIndex: 0 },
    { id: "intake",    label: "Intake sheets completed",   done: !stepStatus.intakeHasPending,    originalIndex: 1 },
    { id: "documents", label: "Documents uploaded",        done: !stepStatus.docsHasPending,      originalIndex: 2 },
  ];

  // If availableSteps is provided, only include steps that are in availableSteps (patient details always included)
  const steps = availableSteps && Array.isArray(availableSteps)
    ? allPossibleSteps.filter(
        (s) => s.id === "patient" || availableSteps.some((st) => st.id === s.id)
      ).map((s) => {
        const foundIdx = availableSteps.findIndex((st) => st.id === s.id);
        return {
          ...s,
          stepIndex: foundIdx !== -1 ? foundIdx : s.originalIndex,
        };
      })
    : allPossibleSteps.map((s) => ({ ...s, stepIndex: s.originalIndex }));

  const allComplete = steps.every((s) => s.done);
  const pendingSteps = steps.filter((s) => !s.done);

  const handlePendencyClick = () => {
    // Go to first pending step
    if (pendingSteps.length > 0) {
      onGoToStep?.(pendingSteps[0].stepIndex);
    }
  };

  return (
    <div
      className="cs__overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose?.();
        }
      }}
    >
      <div className="cs__card">
        {/* Top-right cross close button */}
        <button
          type="button"
          className="cs__close-cross"
          onClick={handleClose}
          aria-label="Close"
          title="Close"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Success icon */}
        <div className="cs__icon-wrap">
          <div className={`cs__icon-circle ${allComplete ? "" : "cs__icon-circle--partial"}`}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <polyline
                points="4 12 9 17 20 6"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {/* Heading */}
        <h2 className="cs__title">Thank you, {firstName}!</h2>
        <p className="cs__subtitle">
          {allComplete
            ? "Here are the options for you to move ahead."
            : "Your submission has been recorded. Some steps are still pending."}
        </p>

        {/* Summary box */}
        <div className="cs__summary">
          <p className="cs__summary-label">SUBMISSION SUMMARY</p>
          {steps.map((step, i) => (
            <div key={i} className="cs__summary-item">
              <div className={`cs__check-icon ${step.done ? "cs__check-icon--done" : "cs__check-icon--pending"}`}>
                {step.done ? (
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                    <polyline
                      points="2 6 5 9 10 3"
                      stroke="white"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                    <line x1="6" y1="2.5" x2="6" y2="6.5"
                      stroke="white" strokeWidth="2" strokeLinecap="round" />
                    <circle cx="6" cy="9" r="0.9" fill="white" />
                  </svg>
                )}
              </div>
              <span className={step.done ? "" : "cs__item-pending-text"}>
                {step.label}
              </span>
              {!step.done && (
                <span className="cs__pending-badge">Pending</span>
              )}
            </div>
          ))}
        </div>

        {/* Pendency message — only when some steps are pending */}
        {!allComplete && (
          <div className="cs__pendency-box">
            <p className="cs__pendency-text">
              Would you like to complete your{" "}
              <button
                className="cs__pendency-link"
                onClick={handlePendencyClick}
              >
                pendency
              </button>
              {" "}or move ahead?
            </p>
          </div>
        )}

        {/* Action buttons */}
        <div className="cs__btn-row">
          <button type="button" className="cs__btn-close" onClick={handleClose}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ marginRight: 6 }}
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            Close
          </button>
          <button className="cs__btn-dashboard" onClick={onGoToDashboard}>
            Go to Dashboard
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"
              strokeLinejoin="round" style={{ marginLeft: 6 }}>
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </button>
          <button className="cs__btn-logout" onClick={onLogout}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"
              strokeLinejoin="round" style={{ marginRight: 6 }}>
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Logout
          </button>
        </div>

        {/* Footer */}
        <p className="cs__footer">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke="#2a9d8f" strokeWidth="2.5" style={{ verticalAlign: "-2px", marginRight: 4 }}>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          Your information is encrypted &amp; secure
        </p>

      </div>
    </div>
  );
};

export default CompletionScreen;